import { useEffect, useMemo, useState } from 'react';
import { StatusBar, ViewProps, Animated, Platform } from 'react-native';
import { Box } from '@/components/base/box';
import { useScreenSize } from '@/hooks/use.screen.size';
import { useAssets } from '@/hooks/use.assets';
import { AppLottieView } from '@/components/base/app.lottie.view';
import styled from 'styled-components/native';

interface Props extends Pick<ViewProps, 'children'> {
  onSplashEnded: () => void;
}
export function SplashView({ children, onSplashEnded }: Props) {
  const [animationEnded, setAnimationEnded] = useState(false);

  useEffect(() => {
    if (animationEnded) {
      onSplashEnded();
    }
  }, [animationEnded, onSplashEnded]);

  return (
    <Container flex={1} alignItems={'center'} justifyContent={'center'}>
      <StatusBar hidden />
      <SplashAnim
        onEnd={() => {
          setAnimationEnded(true);
        }}
      />
      {children}
    </Container>
  );
}

const SplashAnimation = styled(AppLottieView)`
  width: ${({ theme }) => theme.hp(60)}px;
  aspect-ratio: 1;
`;

function SplashAnim({ onEnd }) {
  const { wp, hp } = useScreenSize();
  const fadeIn = useMemo(() => new Animated.Value(0), []);
  const assets = useAssets();

  useEffect(() => {
    let timerId;
    const animation = Animated.timing(fadeIn, {
      toValue: 1,
      duration: 3000,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      timerId = setTimeout(() => {
        onEnd();
      }, 1000);
    });
    return () => {
      animation.stop();
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  return (
    <Box alignItems={'center'} justifyContent={'center'} flex={1}>
      <Box alignItems={'center'}>
        <StatusBar hidden />
        <SplashAnimation source={assets.animations.splash} loop={false} />
        <Animated.Image
          style={{
            width: hp(20),
            height: 'auto',
            maxWidth: wp(50),
            aspectRatio: 3 / 4,
            opacity: fadeIn,
          }}
          resizeMode="contain"
          source={assets.images.en_logo}
        />
      </Box>
    </Box>
  );
}

const Container = styled(Box)`
  background-color: white;
`;
